import { useState, useEffect } from 'react';
import { bearerHeaders } from '../api.ts';
import { getTheme, useThemeMode } from '../theme';
import { api } from '../api.ts';
import type { ApplicationContract } from '../api.ts';
import { GastjaktsAvtal, LegalContractDocument } from '../ApplicationSystem';
import { ContractPayButton } from '../components/ContractPayButton';

function resolveUrl(path: string): string {
  const base = (import.meta.env.VITE_API_URL as string) || '';
  return path.startsWith('http') ? path : `${base}${path}`;
}

export function AppContractFlow({contract,markName,onClose}:{contract:ApplicationContract;markName:string;onClose:()=>void;onComplete?:()=>void}){
  const{mode}=useThemeMode();
  const T=getTheme(mode);
  const[signName,setSignName]=useState('');
  const[busy,setBusy]=useState(false);
  const[step,setStep]=useState<'contract'|'pay'>(
    contract.status==='signed'&&contract.paymentStatus==='unpaid'?'pay':'contract'
  );
  const[markAmount,setMarkAmount]=useState<number>(0);
  const[signedContract,setSignedContract]=useState<ApplicationContract|null>(null);

  // Markens standardavgift används om avtalet skapades utan belopp
  useEffect(()=>{
    if(!contract.markId)return;
    const markId=contract.markId;
    const headers:Record<string,string>={'Content-Type':'application/json','X-Api-Key':'dev-secret'};
    const token=localStorage.getItem('qid_access_token');
    if(token)Object.assign(headers,bearerHeaders(token));
    fetch(resolveUrl('/graphql'),{method:'POST',headers,body:JSON.stringify({query:`query M($id:ID!){mark(id:$id){swishAmount}}`,variables:{id:markId}})})
      .then(r=>r.json()).then(d=>{if(d.data?.mark)setMarkAmount(d.data.mark.swishAmount||0);})
      .catch(()=>{});
  },[contract.markId]);

  // Use fresh signed contract data if available, otherwise fall back to original contract prop
  const activeContract=signedContract||contract;
  const effectiveAmount=activeContract.amount||markAmount;

  async function signContract(){
    if(!signName.trim())return;
    setBusy(true);
    try{
      const updated=await api.signApplicationContract(contract.id,signName);
      setSignedContract(updated);
      setStep('pay');
    }catch(e:any){alert(e.message);}
    setBusy(false);
  }

  return(
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.75)',zIndex:3000,display:'flex',alignItems:'center',justifyContent:'center',padding:'16px'}} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:8,padding:'20px 24px',width:'100%',maxWidth:700,maxHeight:'94vh',overflowY:'auto'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
          <h3 style={{color:T.text,margin:0,fontSize:16,fontWeight:700}}>{step==='contract'?'Avtalshandling':'Betalning'}</h3>
          <button onClick={onClose} style={{background:'none',border:'none',color:T.textSub,cursor:'pointer',fontSize:22}}>×</button>
        </div>

        {step==='contract'?(
          <>
            <div style={{marginBottom:20}}>
              {(contract.contractType==='hunt'||contract.contractType==='hunt_package')?(
                <GastjaktsAvtal
                  markName={markName}
                  huntName={contract.huntName||undefined}
                  huntDates={contract.huntDates||undefined}
                  numHunts={contract.numHunts||1}
                  isPackage={contract.contractType==='hunt_package'}
                  amount={contract.amount??0}
                  pricePerHunt={contract.pricePerHunt||undefined}
                  signatureName={activeContract.signatureName||undefined}
                  signedAt={activeContract.signedAt||undefined}
                  applicantEmail={contract.applicantEmail}
                  contractRef={`QV-GJ-${contract.id.slice(-6).toUpperCase()}`}
                  createdAt={contract.createdAt}
                  showSignaturePlaceholder={contract.status==='pending_signature'}
                />
              ):(
                <LegalContractDocument
                  contractText={contract.contractText}
                  markName={markName}
                  amount={contract.amount??0}
                  signatureName={activeContract.signatureName||undefined}
                  signedAt={activeContract.signedAt||undefined}
                  applicantEmail={contract.applicantEmail}
                  contractRef={`QV-${contract.id.slice(-6).toUpperCase()}`}
                  createdAt={contract.createdAt}
                  showSignaturePlaceholder={contract.status==='pending_signature'}
                />
              )}
            </div>

            {contract.status==='pending_signature'?(
              <>
                <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:6,padding:16,marginBottom:16}}>
                  <div style={{color:T.textSub,fontSize:11,fontWeight:600,letterSpacing:'0.08em',textTransform:'uppercase' as const,marginBottom:8}}>Signera avtalet med ditt fullständiga namn</div>
                  <input value={signName} onChange={e=>setSignName(e.target.value)} placeholder="Förnamn Efternamn" style={{width:'100%',background:T.bg,border:`1px solid ${T.border}`,borderRadius:4,padding:'12px 14px',color:T.text,fontSize:16,outline:'none',boxSizing:'border-box' as const}}/>
                  <div style={{color:T.textMuted,fontSize:11,marginTop:8}}>Genom att signera bekräftar du att du tagit del av och godkänner samtliga villkor i avtalet, samt förbinder dig att erlägga angivet belopp.</div>
                </div>
                <div style={{display:'flex',gap:12}}>
                  <button onClick={onClose} style={{background:T.surface,color:T.text,border:`1px solid ${T.border}`,borderRadius:5,padding:'12px 20px',cursor:'pointer',flex:1}}>Avbryt</button>
                  <button onClick={signContract} disabled={busy||!signName.trim()} style={{background:T.amber,color:'#000',border:'none',borderRadius:5,padding:'12px 20px',fontWeight:700,cursor:'pointer',flex:2}}>{busy?'Signerar...':'Signera avtal'}</button>
                </div>
              </>
            ):(
              <div style={{textAlign:'center',padding:16}}>
                <div style={{color:'#4CAF50',fontSize:14,fontWeight:700,marginBottom:8}}>✓ Avtal signerat av {activeContract.signatureName}</div>
                <div style={{color:T.textSub,fontSize:12}}>{activeContract.signedAt?new Date(activeContract.signedAt).toLocaleDateString('sv-SE'):''}</div>
                {activeContract.paymentStatus==='unpaid'&&(
                  <button onClick={()=>setStep('pay')} style={{background:'#4CAF50',color:'#fff',border:'none',borderRadius:5,padding:'12px 24px',fontWeight:700,cursor:'pointer',marginTop:16}}>Betala med kort</button>
                )}
              </div>
            )}
          </>
        ):(
          <>
            <ContractPayButton contractId={contract.id} amount={effectiveAmount??0}/>
          </>
        )}
      </div>
    </div>
  );
}
